import { useFlags } from 'launchdarkly-react-client-sdk';
import { isNil } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import FormHeader from '../components/Forms/Parts/v2/FormHeader';
import Loader from '../components/LoaderV2';
import { useAuthState } from '../providers/authState';
import { useCookie } from '../providers/cookies';
import { useTranslation } from '../providers/i18next';
import { RETURN_SIGNIN_KEY, useReturnValue } from '../providers/returnValue';
import { RETURN_ROUTE } from '../routes/constants';
import { checkHasBrandingData } from '../utils/checkHasBrandingData';

const LOGIN_SOURCE_COOKIE = 'login_source';
const LOGIN_SOURCE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days in seconds

/**
 * MP-6664 - why this page redirects itself.
 *
 * This page used to sign in and then rely on <GuestRoute> noticing the new auth
 * state and redirecting to RETURN_ROUTE for us. That is not reliable.
 * GuestRoute locks in its routing decision MAX_TIMEOUT (4s) after it mounts, so
 * a sign-in that settles later than that leaves this page locked in "guest"
 * mode permanently. <LoadingRoute> unmounts its children whenever the auth or
 * session state is unsettled and remounts them once it settles, so the locked
 * GuestRoute happily mounts this page again, the mount effect fires another
 * signInWithCustomToken, and that sign-in calls cleanupSignout() which throws
 * away the session id it just created - which unsettles the state and starts
 * the whole thing over. The user watches a spinner while the tab replays
 * signInWithCustomToken and orphans a new authSession document every few
 * seconds.
 *
 * The invited-parent invoice flow trips the 4s window because the backend is
 * still adopting the identity - applying custom claims, flipping emailVerified
 * - while this page is mounted, which is why it shows up here and not on the
 * faster custom-token logins.
 *
 * Two things keep us out of that loop:
 *  1. we navigate to RETURN_ROUTE ourselves once the sign-in succeeds, the same
 *     way every other guest page does (SigninForm, SignupForm,
 *     VerifyEmailForm), instead of depending on the GuestRoute window.
 *  2. we remember which token we already signed in with, so if we do get
 *     remounted we re-issue the redirect instead of signing in all over again.
 *
 * The guard is module state on purpose: the guarantee we need is one sign-in
 * per token per page load, which has to outlive any single mount of this
 * component. A real page reload resets it, which is what we want - a reload is
 * how a user recovers a session that never completed.
 */
let signedInToken: string | undefined;
let pendingToken: string | undefined;

/** Test seam: clears the per-page-load sign-in guard. */
export const resetSignInGuard = () => {
  signedInToken = undefined;
  pendingToken = undefined;
};

const CustomTokenLoginPage = () => {
  const navigate = useNavigate();
  const { txn } = useTranslation();
  const { brandingData, returnValue, getReturnState } = useReturnValue();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { signInWithCustomToken } = useAuthState();
  const [, setLoginSource] = useCookie(LOGIN_SOURCE_COOKIE);
  const { isLoginSourceRedirectEnabled } = useFlags();

  const hasBranding = useMemo(() => {
    return checkHasBrandingData(brandingData);
  }, [brandingData]);

  // Try to login with the custom token in the URL hash data.
  //
  // Feature flag: isLoginSourceRedirectEnabled
  // Before signing in, persist the login source so the signin page can
  // redirect users back to their app's login page if their session
  // expires later. The cookie is written synchronously before the async
  // sign-in call to guarantee it is set before any navigation occurs.
  useEffect(() => {
    if (isLoginSourceRedirectEnabled) {
      const retUrl = returnValue?.ret;
      if (retUrl) {
        try {
          const fullUrl = retUrl.startsWith('//') ? `https:${retUrl}` : retUrl;
          const origin = new URL(fullUrl).origin;
          setLoginSource(`${origin}/accounts/login`, {
            path: '/',
            maxAge: LOGIN_SOURCE_MAX_AGE,
          });
        } catch (e) {
          // ignore invalid URLs — cookie is best-effort
        }
      }
    }

    const signinData = {
      [RETURN_SIGNIN_KEY]: true,
    };

    const trySignIn = async (token: string) => {
      try {
        return await signInWithCustomToken(token, signinData);
      } catch (error) {
        return undefined;
      }
    };

    const goToReturn = () => navigate(RETURN_ROUTE, { replace: true });

    const login = async () => {
      const customToken = getReturnState().customToken;

      // Already signed in with this token earlier in this page load. The
      // redirect is the only thing still owed to the user, so just re-issue it.
      if (customToken && signedInToken === customToken) {
        goToReturn();
        return;
      }

      // A sign-in for this token is still in flight from an earlier mount. It
      // will redirect when it settles; signing in again would only orphan
      // another session.
      if (customToken && pendingToken === customToken) {
        return;
      }

      pendingToken = customToken;
      const result = await trySignIn(customToken);
      pendingToken = undefined;

      if (isNil(result) || result.errorCode) {
        setLoading(false);
        setError(true);
        return;
      }

      signedInToken = customToken;
      // Do not wait for <GuestRoute> to notice - see the note above. If this
      // mount has already been torn down the navigate is a no-op, and the
      // guard above re-issues it from the next mount.
      goToReturn();
    };

    login();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <Loader hasBranding={hasBranding} />;
  }

  if (error) {
    return (
      <FormHeader
        text={txn('Oops, something went wrong')}
        subtext={txn('We could not process your request')}
      />
    );
  }

  return <div>&nbsp;</div>;
};

export default CustomTokenLoginPage;
